import { sampleRandom, shuffle } from '@gametailors/web-core/misc';
import { WordToKlankConverter } from '../../utils/klank.utils';

/**
 * Generates a multiple-choice quiz with 4 phoneme (klank) options for transforming the current word into the target word. A transformation can involve adding, removing, or replacing a single phoneme.
 *
 * This function breaks both words into phonemes, identifies the correct phoneme needed to progress toward the target,
 * generates 3 distractor options from phoneme variants, and shuffles them together.
 *
 * @param currentWord - The current word in its present state
 * @param targetWord - The goal word to transform into
 * @returns An array of 4 phoneme options, each with a `klank` string and `correct` boolean flag
 * @throws Error if no correct klank can be found between the current and target words
 */
export function generateKlankOptions(currentWord: string, targetWord: string): { klank: string; correct: boolean }[] {
  const currentKlanken = WordToKlankConverter.breakupWord(currentWord);
  const targetKlanken = WordToKlankConverter.breakupWord(targetWord);

  const correctKlank = findCorrectKlank(currentKlanken, targetKlanken);
  if (!correctKlank) throw new Error('No correct klank found between current and target words.');

  const availableKlanken = WordToKlankConverter.getKlankVariantsFor(correctKlank);

  const distractors = sampleRandom([...availableKlanken], 3);
  const options = shuffle([...distractors, correctKlank]);

  return options.map((k) => ({ klank: k, correct: k === correctKlank }));
}

/**
 * Identifies which phoneme needs to be added to the current word to move toward the target word.
 *
 * Compares the frequency of phonemes in both words to find the first phoneme that appears more often
 * in the target word than in the current word. This represents the next phoneme the player needs to add.
 *
 * @param current - Array of phonemes in the current word
 * @param target - Array of phonemes in the target word
 * @returns The phoneme that needs to be added, or `undefined` if no additional phoneme is needed
 */
function findCorrectKlank(current: string[], target: string[]): string | undefined {
  const currentFreq = createFrequencyMap(current);
  const targetFreq = createFrequencyMap(target);

  for (const klank of Object.keys(targetFreq)) {
    if ((currentFreq[klank] ?? 0) < targetFreq[klank]) {
      return klank;
    }
  }

  return undefined;
}

/**
 * Creates a frequency map counting the occurrences of each phoneme in the array.
 *
 * @param klanken - Array of phonemes to count
 * @returns A map where keys are phonemes and values are their occurrence counts (e.g., { "aa": 2, "b": 1 })
 */
function createFrequencyMap(klanken: string[]): Record<string, number> {
  return klanken.reduce(
    (freq, klank) => {
      freq[klank] = (freq[klank] ?? 0) + 1;
      return freq;
    },
    {} as Record<string, number>,
  );
}
